import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

const TAXONOMY_TYPES = [
  { key: 'country', label: 'Country' },
  { key: 'state', label: 'State' },
  
  { key: 'level_of_study', label: 'Level of Study' },
  { key: 'field_of_study', label: 'Field of Study' },
  { key: 'subject', label: 'Subject' },
  { key: 'mode_of_study', label: 'Mode of Study' },
  { key: 'intake', label: 'Intake' },
  { key: 'institution_type', label: 'Institution Type' },
  { key: 'scholarship_type', label: 'Scholarship Type' },
  { key: 'article_category', label: 'Article Category' }
];

export const TaxonomyManager = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('level_of_study');
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ label: '', slug: '', parent_id: '', enabled: true, sort_order: 0 });

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchAllItems();
  }, []);

  useEffect(() => {
    filterItemsByType();
  }, [activeTab, allItems]);

  const fetchAllItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/master-taxonomy`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch');

      const data = await response.json();
      setAllItems(data.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filterItemsByType = () => {
    const filtered = allItems
      .filter(item => item.type === activeTab)
      .sort((a, b) => a.sort_order - b.sort_order);
    setItems(filtered);
  };

  const handleSave = async () => {
    if (!formData.label.trim()) {
      alert('Label is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const slug = formData.slug || formData.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      
      const payload = {
        type: activeTab,
        label: formData.label,
        slug: slug,
        parent_id: formData.parent_id || null,
        enabled: formData.enabled,
        sort_order: formData.sort_order
      };

      const url = editingId 
        ? `${backendUrl}/api/admin/master-taxonomy/${editingId}`
        : `${backendUrl}/api/admin/master-taxonomy`;

      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to save');
      }

      resetForm();
      fetchAllItems();
    } catch (err) {
      alert(err.message || 'Failed to save taxonomy item');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      label: item.label,
      slug: item.slug,
      parent_id: item.parent_id || '',
      enabled: item.enabled,
      sort_order: item.sort_order
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/master-taxonomy/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete');

      fetchAllItems();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('This will add default taxonomy items. Existing items will not be affected. Continue?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/master-taxonomy/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to seed');

      const data = await response.json();
      alert(data.message);
      fetchAllItems();
    } catch (err) {
      alert('Failed to seed defaults');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({ label: '', slug: '', parent_id: '', enabled: true, sort_order: items.length * 10 });
  };

  const toggleEnabled = async (item) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/master-taxonomy/${item.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...item,
          enabled: !item.enabled
        })
      });

      if (!response.ok) throw new Error('Failed to update');

      fetchAllItems();
    } catch (err) {
      alert('Failed to toggle status');
    }
  };

  // Get parent options for subjects (field_of_study items)
  const getParentOptions = () => {
    if (activeTab !== 'subject') return [];
    return allItems.filter(item => item.type === 'field_of_study' && item.enabled);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading taxonomy...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#111827] mb-2" data-testid="taxonomy-title">Master Taxonomy</h1>
            <p className="text-[#6B7280]">Manage global taxonomy items used across the platform</p>
          </div>
          <button
            onClick={handleSeedDefaults}
            className="px-4 py-2 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#111827] font-semibold rounded-lg text-sm"
            data-testid="seed-defaults-btn"
          >
            Seed Defaults
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="flex flex-wrap border-b-2 border-[#E5E7EB]">
            {TAXONOMY_TYPES.map(type => (
              <button
                key={type.key}
                onClick={() => { setActiveTab(type.key); resetForm(); }}
                className={`px-4 py-3 text-sm font-semibold transition-colors ${
                  activeTab === type.key
                    ? 'bg-[#1E40AF] text-white'
                    : 'text-[#6B7280] hover:bg-[#F3F4F6]'
                }`}
                data-testid={`tab-${type.key}`}
              >
                {type.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Add/Edit Form */}
            <div className="bg-[#F8FAFC] border-2 border-[#E5E7EB] rounded-lg p-4 mb-6">
              <h3 className="text-sm font-bold text-[#111827] mb-3">
                {editingId ? 'Edit Item' : 'Add New Item'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Label *</label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) => setFormData({...formData, label: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                    placeholder="e.g., Bachelor's Degree"
                    data-testid="input-label"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                    placeholder="auto-generated"
                    data-testid="input-slug"
                  />
                </div>
                {activeTab === 'subject' && (
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Parent Field</label>
                    <select
                      value={formData.parent_id}
                      onChange={(e) => setFormData({...formData, parent_id: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                      data-testid="input-parent"
                    >
                      <option value="">No Parent</option>
                      {getParentOptions().map(parent => (
                        <option key={parent.id} value={parent.id}>{parent.label}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] text-sm"
                    data-testid="input-sort-order"
                  />
                </div>
              </div>
              <div className="flex items-center gap-4 mt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
                    className="w-4 h-4"
                    data-testid="input-enabled"
                  />
                  <span className="text-sm text-[#6B7280]">Enabled</span>
                </label>
                <div className="flex-1"></div>
                {editingId && (
                  <button
                    onClick={resetForm}
                    className="px-4 py-2 text-[#6B7280] hover:text-[#111827] font-semibold text-sm"
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg text-sm disabled:opacity-50"
                  data-testid="save-btn"
                >
                  {saving ? 'Saving...' : (editingId ? 'Update' : 'Add')}
                </button>
              </div>
            </div>

            {/* Items Table */}
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="taxonomy-table">
                <thead>
                  <tr className="border-b-2 border-[#E5E7EB]">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Order</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Label</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Slug</th>
                    {activeTab === 'subject' && (
                      <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Parent</th>
                    )}
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={activeTab === 'subject' ? 6 : 5} className="py-8 text-center text-[#6B7280]">
                        No items found. Add one above or click "Seed Defaults".
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.id} className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC]" data-testid={`row-${item.id}`}>
                        <td className="py-3 px-4 text-sm text-[#6B7280]">{item.sort_order}</td>
                        <td className="py-3 px-4 text-sm font-medium text-[#111827]">{item.label}</td>
                        <td className="py-3 px-4 text-sm text-[#6B7280]">{item.slug}</td>
                        {activeTab === 'subject' && (
                          <td className="py-3 px-4 text-sm text-[#6B7280]">
                            {allItems.find(p => p.id === item.parent_id)?.label || '-'}
                          </td>
                        )}
                        <td className="py-3 px-4">
                          <button
                            onClick={() => toggleEnabled(item)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              item.enabled
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                            data-testid={`toggle-${item.id}`}
                          >
                            {item.enabled ? 'Enabled' : 'Disabled'}
                          </button>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(item)}
                              className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm"
                              data-testid={`edit-${item.id}`}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="text-red-600 hover:text-red-800 font-semibold text-sm"
                              data-testid={`delete-${item.id}`}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default TaxonomyManager;
